// ./src/components/features/Events/controls/ScheduleModeField.tsx
//
// "One day / Multi-day / Repeats" chooser — segmented control on the shared
// (name, value) form contract. "Repeats" is only offered when the selected event
// type can recur (pass `allowRecurring`).

import React, { useMemo } from 'react';
import { Group, Text } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { SegmentedField, SegmentedFieldProps } from '@/components/generic';

// Local mirror of the schedule-mode values (the canonical enum lives in the
// wizard types; kept internal here to avoid a duplicate export).
const MODE = { SINGLE: 'single', RECURRING: 'recurring', AGENDA: 'agenda' };

export interface ScheduleModeFieldProps extends Omit<SegmentedFieldProps, 'data'> {
  /** Include the "Repeats" option (recurring). Default false. */
  allowRecurring?: boolean;
  /**
   * Show "Repeats" but DISABLED, with a lock — the plan does not include recurring events.
   *
   * ⚠ Shown rather than hidden, deliberately. `allowRecurring: false` removes the option because
   * the event TYPE cannot recur (a wedding does not repeat) — there is nothing to sell there. A
   * PLAN limit is the opposite: the option is real and buyable, so hiding it would mean the host
   * never learns the product does it.
   */
  recurringLocked?: boolean;
}

export const ScheduleModeField: React.FC<ScheduleModeFieldProps> = ({
  allowRecurring = false,
  recurringLocked = false,
  ...props
}) => {
  const { t } = useTranslation(['events']);
  const data = useMemo(() => {
    // Ordered by how long the event spans: one day → multi-day → repeating.
    const items: { label: React.ReactNode; value: string; disabled?: boolean }[] = [
      { label: t('events:schedule.oneDay', { defaultValue: 'One day' }), value: MODE.SINGLE },
      { label: t('events:schedule.multiDay', { defaultValue: 'Multi-day' }), value: MODE.AGENDA },
    ];
    if (allowRecurring) {
      const label = t('events:schedule.repeats', { defaultValue: 'Repeats' });

      items.push(
        recurringLocked
          ? {
              // ⚠ The lock travels in the LABEL, not a tooltip — touch devices have no hover, so a
              // `title=` would be invisible to most hosts. The reason goes under the control, from
              // the caller's <PlanLockNote>.
              label: (
                <Group gap={4} wrap="nowrap" justify="center">
                  <IconLock size={13} />
                  <Text component="span" size="md">
                    {label}
                  </Text>
                </Group>
              ),
              value: MODE.RECURRING,
              disabled: true,
            }
          : { label, value: MODE.RECURRING }
      );
    }
    return items;
  }, [allowRecurring, recurringLocked, t]);

  // Mantine's SegmentedControl accepts a ReactNode label and a per-item `disabled`; the shared
  // SegmentedField types `data` narrowly, so this is the one place the wider shape is asserted.
  return <SegmentedField data={data as never} {...props} />;
};
